import * as vp from 'vopay';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

const useFileDownload = (): {
  downloadJson: (data: unknown, fileName: string) => void;
  downloadPdf: (data: string, fileName: string) => void;
  downloadCsv: (data: string, fileName: string) => void;
  downloadCsvFile: (id: string, fileName: string) => void;
  downloadXls: (id: string, fileName: string) => void;
  downloadDoc: (id: string, fileName: string) => void;
  downloadTxt: (id: string, fileName: string) => void;
  downloadXml: (id: string, fileName: string) => void;
  downloadXlsxFileType: (data: string, fileName: string) => void;
  // downloadJsonFile: (id: string, fileName: string) => void;
  downloadPdfTable: (id: string, fileName: string) => void;
  downloadPdfFileType: (data: string, fileName: string) => void;
  downloadCsvFileType: (data: string, fileName: string) => void;
  printFile: (id: string) => void;
  downloadPdfPage: (id: string, fileName: string) => void;
} => {
  const downloadJson = (data: unknown, fileName: string) => {
    vp.downloadBlob({
      data: JSON.stringify(data, null, 4),
      fileName: `${fileName.replace('.json', '')}.json`,
      fileType: 'text/json',
    });
  };
  const downloadPdfFileType = (data: string, fileName: string) => {
    vp.downloadBase64DataUri({
      data,
      fileName: `${fileName.replace('.pdf', '')}.pdf`,
      fileType: 'application/pdf',
    });
  };

  const downloadCsvFileType = (data: string, fileName: string) => {
    vp.downloadBase64DataUri({
      data,
      fileName: `${fileName.replace('.csv', '')}.csv`,
      fileType: 'application/octet-stream',
    });
  };
  const downloadXlsxFileType = (data: string, fileName: string) => {
    vp.downloadBase64DataUri({
      data,
      fileName: `${fileName.replace('.xlsx', '')}.xlsx`,
      fileType: 'application/vnd.ms-excel',
    });
  };

  const downloadPdf = (data: string, fileName: string) => {
    const downloadLink = document.createElement('a');
    downloadLink.href = data;
    downloadLink.setAttribute('download', `${fileName.replace('.pdf', '')}.pdf`);
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const downloadCsv = (data: string, fileName: string) => {
    const downloadLink = document.createElement('a');
    downloadLink.href = data;
    downloadLink.setAttribute('download', `${fileName.replace('.csv', '')}.csv`);
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };
  const downloadCsvFile = (id: string, fileName: string) => {
    const table = document.getElementById(id);

    if (!table) {
      console.error(`Table with ID ${id} not found.`);
      return;
    }

    let csvContent = '';

    // Iterate through each row in the table
    const rows = table.querySelectorAll('tr');
    rows.forEach((row) => {
      const cells = row.querySelectorAll('th, td');

      // Iterate through each cell in the row
      cells.forEach((cell, index) => {
        let cellValue = (cell.textContent || '').trim();

        // Escape cell value if it contains commas or double quotes
        if (cellValue.includes(',') || cellValue.includes('"')) {
          cellValue = `"${cellValue.replace(/"/g, '""')}"`;
          // Additional escaping for comma within the cell value
        } else if (cellValue.length > 0) {
          // Add quotes to non-empty cells
          cellValue = `"${cellValue}"`;
        }

        if (cellValue.trim() === '-' || cellValue.trim() === '"-"') {
          cellValue = '';
        }
        // Add cell value to CSV content
        csvContent += cellValue;

        // Add comma if it's not the last cell in the row
        if (index < cells.length - 1) {
          csvContent += ',';
        }
      });

      // Add newline character after each row
      csvContent += '\r\n';
    });

    // Create and trigger download
    csvContent = csvContent.replaceAll('-,', ',');
    const file = new Blob([csvContent], { type: 'text/csv' });
    const downloadLink = document.createElement('a');
    downloadLink.download = `${fileName}.csv`;
    downloadLink.href = window.URL.createObjectURL(file);
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    downloadLink.click();

    // Clean up
    document.body.removeChild(downloadLink);
  };

  const downloadXls = (id: string, fileName: string) => {
    const table = document.getElementById(id);
    if (!table) return;

    const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:x="urn:schemas-microsoft-com:office:excel"
          xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>${fileName}</x:Name>
                <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <meta charset="UTF-8">
      </head>
      <body>
        ${table.outerHTML}
      </body>
    </html>
  `;

    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);

    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = `${fileName}.xls`;

    document.body.appendChild(downloadLink);
    downloadLink.click();

    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);
  };

  const downloadDoc = (id: string, fileName: string) => {
    const tableElement = document.getElementById(id);
    if (!tableElement) return;

    const tableHTML = tableElement.outerHTML;
    const header = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:w="urn:schemas-microsoft-com:office:word"
          xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8"></head><body>`;
    const footer = `</body></html>`;

    const htmlContent = header + tableHTML + footer;

    const blob = new Blob(['\ufeff', htmlContent], {
      type: 'application/msword',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadTxt = (id: string, fileName: string) => {
    const table = document.getElementById(id)?.outerHTML;
    const strippedHtml = table
      ?.replaceAll('</tr>', '\r')
      .replaceAll('</th>', ',')
      .replaceAll('</td>', ',')
      .replaceAll('</th>', ',')
      .replaceAll(/<[^>]+>/g, '')
      .replaceAll(',,', ',');
    const file = `${fileName}.txt`;

    const txtFile = new Blob([strippedHtml as BlobPart], { type: 'text/plain' });
    const downloadLink = document.createElement('a');
    downloadLink.download = file;
    downloadLink.href = window.URL.createObjectURL(txtFile);
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    downloadLink.click();
  };

  const downloadXml = (id: string, fileName: string) => {
    const table = document.getElementById(id);
    if (!table) return;

    const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<root>
  <tableData><![CDATA[${table.outerHTML}]]></tableData>
</root>`;

    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);

    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = `${fileName}.xml`;

    document.body.appendChild(downloadLink);
    downloadLink.click();

    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);
  };

  const downloadPdfTable = (id: string, fileName: string) => {
    const pdf = new jsPDF();

    autoTable(pdf, {
      html: `#${id}`,
      startY: 10,
      margin: { left: 10, right: 10 },
      styles: { cellPadding: 2, fontSize: 8, overflow: 'linebreak' },
      headStyles: { halign: 'left' },
      bodyStyles: { halign: 'left' },
    });

    pdf.save(`${fileName}.pdf`);
  };
  const downloadPdfPage = (id: string, fileName: string) => {
    const doc = new jsPDF();

    const div = document.getElementById(id);
    const hiddenElements = div?.getElementsByClassName('hiddenFormsPrint');
    const img = div?.getElementsByTagName('img');
    if (hiddenElements) {
      for (const element of hiddenElements) {
        const currentElement = element as HTMLElement;
        currentElement.style.display = 'none';
      }
    }
    if (img) {
      for (const image of img) {
        const currentImage = image as HTMLImageElement;
        currentImage.style.display = 'none';
      }
    }
    doc.html(String(div?.outerHTML), {
      callback(doc) {
        const tableCallBack = document.getElementById(id);
        const hiddenElementsCallBack = tableCallBack?.getElementsByClassName('hiddenFormsPrint');
        const img = tableCallBack?.getElementsByTagName('img');
        doc.save(`${fileName}.pdf`);
        if (hiddenElementsCallBack) {
          for (const element of hiddenElementsCallBack) {
            const currentElement = element as HTMLElement;
            currentElement.style.display = 'initial';
          }
        }
        if (img) {
          for (const image of img) {
            const currentImage = image as HTMLImageElement;
            currentImage.style.display = 'initial';
          }
        }
      },
      margin: [10, 10, 10, 10],
      autoPaging: 'text',
      x: 0,
      y: 0,
      width: 150,
      windowWidth: 675,
    });
  };
  const getStyleSheets = () => {
    const styleSheets = Array.from(document.styleSheets);
    const styles = styleSheets.map((styleSheet) => {
      if (styleSheet.href) {
        // External stylesheets
        return styleSheet.href;
      }
      // Inline styles
      const rules = Array.from(styleSheet.cssRules).map((rule) => rule.cssText);
      return rules.join('\n');
    });
    return styles.join('\n');
  };
  const getScripts = () => {
    const scripts = Array.from(document.scripts);
    const scriptSrcs = scripts.map((script) => script.src);
    return scriptSrcs.join('\n');
  };
  function isURL(str: string) {
    const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
    return urlPattern.test(str);
  }
  const printFile = (id: string) => {
    const table = document.getElementById(id)?.outerHTML;
    if (!table) return;

    const overlay = document.createElement('div');
    overlay.id = 'printOverlay';
    Object.assign(overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      background: 'rgba(0,0,0,0.4)',
      zIndex: '9999',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontSize: '18px',
      fontFamily: 'sans-serif',
    });
    overlay.textContent = 'Preparing print view...';
    document.body.appendChild(overlay);

    const styles = getStyleSheets();
    const scripts = getScripts();
    const isExternal = isURL(styles);

    const newWindow = window.open('', '_blank');
    if (!newWindow) return;

    const html = `
    <html>
      <head>
        ${isExternal ? `<link rel="stylesheet" href="${styles}">` : `<style>${styles}</style>`}
        <style>.hiddenFormsPrint { display: none; }</style>
      </head>
      <body>
        ${table}
        <script>${scripts}</script>
      </body>
    </html>
  `;

    newWindow.document.write(html);
    newWindow.document.close();

    newWindow.onload = () => {
      newWindow.print();
      newWindow.onafterprint = () => {
        newWindow.close();
        document.getElementById('printOverlay')?.remove();
      };

      setTimeout(() => {
        newWindow.close();
        document.getElementById('printOverlay')?.remove();
      }, 3000);
    };
  };

  return {
    downloadJson,
    downloadPdf,
    downloadCsv,
    downloadCsvFile,
    downloadXlsxFileType,
    downloadXls,
    downloadDoc,
    downloadTxt,
    downloadXml,
    downloadPdfTable,
    downloadPdfFileType,
    downloadCsvFileType,
    printFile,
    downloadPdfPage,
  };
};

export default useFileDownload;
